// /reports/staff-expense — Staff Expense Tracking (PAGES.md §3.94)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Avatar, Badge } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { StaffExpenseRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = StaffExpenseRow & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    {
      key: 'name',
      header: 'Staff',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          <Avatar name={row.name} size="sm" />
          <div>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</p>
            {row.is_high_spender ? <Badge variant="warning" size="sm">High spender</Badge> : null}
          </div>
        </div>
      ),
    },
    { key: 'submissions', header: 'Submitted', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.submissions}</span> },
    { key: 'approved_count', header: 'Approved', numeric: true, render: (_v, row) => <span className="text-success-600 tabular-nums">{row.approved_count}</span> },
    { key: 'rejected_count', header: 'Rejected', numeric: true, render: (_v, row) => <span className="text-danger-600 tabular-nums">{row.rejected_count}</span> },
    { key: 'total_approved', header: 'Total Approved', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total_approved, currency)}</span> },
    { key: 'avg_per_submission', header: 'Avg', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.avg_per_submission, currency)}</span> },
  ];
}

export function StaffExpenseReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'staff-expense',
    filter,
    queryFn: (f) => reportsApi.getStaffExpense(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.rows ?? []).map((r) => ({ ...r, id: r.user_id })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `staff-expense-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Staff', get: (r: Row) => r.name },
          { header: 'Submissions', get: (r: Row) => r.submissions },
          { header: 'Approved', get: (r: Row) => r.approved_count },
          { header: 'Rejected', get: (r: Row) => r.rejected_count },
          { header: `Total Approved (${symbol})`, get: (r: Row) => (r.total_approved / subunit).toFixed(csvDecimals) },
          { header: `Avg / Submission (${symbol})`, get: (r: Row) => (r.avg_per_submission / subunit).toFixed(csvDecimals) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Staff Expenses', `${rows.length} staff tracked • Top submitter: ${rows[0]?.name ?? '—'}`);
    }
  };

  return (
    <ReportPageShell
      title="Staff Expense Tracking"
      subtitle="Per-staff submission, approval and rejection patterns."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Staff Expenses' }]}
      accent="warning-danger"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={0} />
      ) : error ? (
        <ReportError />
      ) : (
        <ReportTable title="Per-Staff Expense Activity" columns={columns} data={rows} emptyTitle="No staff expense data for selected period" totalCount={rows.length} />
      )}
    </ReportPageShell>
  );
}
